import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Extracts bucket and path from a Supabase storage URL.
 * Handles both /object/public/ and /object/authenticated/ formats.
 */
function parseStorageUrl(url: string): { bucket: string; path: string } | null {
    try {
        const match = url.match(/\/storage\/v1\/object\/(?:public|authenticated)\/([^/]+)\/(.+)/);
        if (match) {
            return { bucket: match[1], path: decodeURIComponent(match[2]) };
        }
    } catch {
        // ignore parse errors
    }
    return null;
}

// In-memory cache to avoid re-signing the same URL
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
const EXPIRY_SECONDS = 3600; // 1 hour
const BUFFER_SECONDS = 300; // refresh 5 min before expiry

/**
 * React hook that converts a Supabase storage URL into a signed URL.
 * Works with private buckets where direct URLs require auth headers.
 *
 * @param storageUrl - Full Supabase storage URL, external URL, or null
 * @returns Signed URL string, or undefined while loading
 */
export function useSignedUrl(storageUrl: string | null | undefined): string | undefined {
    const [signedUrl, setSignedUrl] = useState<string | undefined>(() => {
        if (!storageUrl) return undefined;
        // External URLs pass through immediately
        if (!storageUrl.includes('/storage/v1/object/')) return storageUrl;
        // Check cache
        const cached = signedUrlCache.get(storageUrl);
        if (cached && Date.now() < cached.expiresAt) return cached.url;
        return undefined;
    });

    useEffect(() => {
        if (!storageUrl) {
            setSignedUrl(undefined);
            return;
        }

        // Not a storage URL — use directly (external URLs, data URIs, blobs, etc.)
        if (!storageUrl.includes('/storage/v1/object/')) {
            setSignedUrl(storageUrl);
            return;
        }

        // Check cache
        const cached = signedUrlCache.get(storageUrl);
        if (cached && Date.now() < cached.expiresAt) {
            setSignedUrl(cached.url);
            return;
        }

        const parsed = parseStorageUrl(storageUrl);
        if (!parsed) {
            setSignedUrl(storageUrl); // can't parse, try original
            return;
        }

        let cancelled = false;

        supabase.storage
            .from(parsed.bucket)
            .createSignedUrl(parsed.path, EXPIRY_SECONDS)
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error || !data?.signedUrl) {
                    setSignedUrl(undefined);
                    return;
                }
                signedUrlCache.set(storageUrl, {
                    url: data.signedUrl,
                    expiresAt: Date.now() + (EXPIRY_SECONDS - BUFFER_SECONDS) * 1000,
                });
                setSignedUrl(data.signedUrl);
            });

        return () => { cancelled = true; };
    }, [storageUrl]);

    return signedUrl;
}
